import { useMemo, useState } from "react";
import { fileUrl } from "@/lib/api";
import BookingStatusBadge from "@/components/ops/BookingStatusBadge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import BookingMessagesPanel from "@/components/messaging/BookingMessagesPanel";
import BookingLogisticsCard from "@/components/booking/BookingLogisticsCard";
import RiskyRenterBadge from "@/components/booking/RiskyRenterBadge";
import BookingExtrasPanel from "@/components/bookings/BookingExtrasPanel";
import BookingActionsMenu from "@/components/bookings/BookingActionsMenu";
import BookingCodeChip from "@/components/booking/BookingCodeChip";
import VehicleSwapDialog from "@/components/booking/VehicleSwapDialog";
import BookingFiscalizeButton from "@/components/booking/BookingFiscalizeButton";
import WhatsAppButton from "@/components/whatsapp/WhatsAppButton";
import Money from "@/components/Money";
import AttributionChips from "@/components/AttributionChips";

// Iter 68 — sticky status-filter chip row. Stored in component state (not
// URL) because the surrounding dashboard tabs already own routing concerns;
// the filter is purely a per-session lens on top of the same data.
const FILTERS = [
  { key: "all",            label: "All" },
  { key: "confirmed",      label: "Confirmed" },
  { key: "pending_approval", label: "Pending approval" },
  { key: "pending_payment", label: "Pending payment" },
  { key: "declined_by_host", label: "Declined" },
  { key: "expired_no_response", label: "Expired" },
  { key: "no_show",        label: "No Show" },
  { key: "cancelled",      label: "Cancelled" },
  { key: "completed",      label: "Completed" },
];

export default function VendorBookingsList({ bookings, depositLoadingId, onRelease, onWithhold, onChanged, unreadByBooking = {}, onUnreadChange }) {
  const [filter, setFilter] = useState("all");
  const counts = useMemo(() => {
    const c = { all: bookings.length };
    for (const b of bookings) {
      const s = (b.status || "").toLowerCase();
      c[s] = (c[s] || 0) + 1;
    }
    return c;
  }, [bookings]);
  const visible = useMemo(
    () => (filter === "all" ? bookings : bookings.filter((b) => (b.status || "").toLowerCase() === filter)),
    [bookings, filter],
  );

  if (bookings.length === 0) {
    return <div className="rounded-2xl border border-dashed border-border p-12 text-center text-neutral-500">No bookings yet.</div>;
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="vendor-bookings-filter">
        {FILTERS.map((f) => {
          const n = counts[f.key] || 0;
          const active = filter === f.key;
          // Hide zero-count chips except "All" so the rail doesn't get noisy.
          if (f.key !== "all" && n === 0) return null;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition ${
                active
                  ? "bg-neutral-900 text-white border-neutral-900"
                  : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400"
              }`}
              data-testid={`vendor-bookings-filter-${f.key}`}
            >
              <span>{f.label}</span>
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${active ? "bg-white/20" : "bg-neutral-100 text-neutral-600"}`}>{n}</span>
            </button>
          );
        })}
      </div>
      {visible.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-neutral-500">
          No bookings match this filter.
        </div>
      ) : (
      <>
      {visible.map((b) => {
        const unread = unreadByBooking[b.id] || 0;
        // Iter 65: 3-sided coloured frame to telegraph status at a glance.
        // confirmed=green, pending_payment=amber, pending=sky, cancelled=rose,
        // completed=slate, no_show=violet (Iter 68 — distinct so vendors can
        // spot no-shows in a long bookings list without scanning the badge).
        const status = (b.status || "").toLowerCase();
        const frameClass =
          status === "confirmed"
            ? "border-y-2 border-l-[6px] border-emerald-500"
            : status === "pending_approval"
            ? "border-y-2 border-l-[6px] border-amber-600"
            : status === "declined_by_host" || status === "expired_no_response"
            ? "border-y-2 border-l-[6px] border-neutral-400"
            : status === "pending_payment"
            ? "border-y-2 border-l-[6px] border-amber-500"
            : status === "cancelled"
            ? "border-y-2 border-l-[6px] border-rose-500"
            : status === "completed"
            ? "border-y-2 border-l-[6px] border-slate-400"
            : status === "no_show"
            ? "border-y-2 border-l-[6px] border-violet-500"
            : "border-y-2 border-l-[6px] border-sky-500";  // generic pending / anything else
        return (
        <div
          key={b.id}
          className={`rounded-2xl border-r border-border bg-white p-5 ${frameClass}`}
          data-testid={`vendor-booking-${b.id}`}
          data-booking-status={status}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-3">
                {b.car?.images?.[0] && (
                  <img
                    src={b.car.images[0]}
                    alt={`${b.car?.make || ""} ${b.car?.model || ""}`.trim()}
                    className="h-14 w-20 sm:h-20 sm:w-28 rounded-xl object-cover shrink-0 ring-1 ring-border"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    data-testid={`booking-car-image-${b.id}`}
                  />
                )}
                <div className="min-w-0">
                  {/* Iter 65: clickable booking code chip — click to copy to clipboard. */}
                  <div className="mb-1.5">
                    <BookingCodeChip bookingId={b.id} />
                  </div>
                  <div className="font-medium flex items-center gap-2 flex-wrap">
                    {b.car?.make} {b.car?.model}
                    {unread > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-blue-600 text-white text-[10px] font-semibold" data-testid={`unread-badge-${b.id}`}>
                        {unread}
                      </span>
                    )}
                    {/* Iter 69 — risky-renter badge surfaces the customer's
                        no-show history right next to the car name so vendors
                        can spot repeat offenders without leaving the list. */}
                    <RiskyRenterBadge customer={b.customer} />
                  </div>
                  <div className="text-sm text-neutral-500">{b.start_date?.slice(0, 10)} → {b.end_date?.slice(0, 10)} · {b.customer?.name}</div>
                </div>
              </div>
              {b.customer?.whatsapp && (
                <div className="mt-1">
                  <WhatsAppButton
                    phone={b.customer.whatsapp}
                    message={`Hi ${b.customer.name || ""}, your RentalCar.al booking #${b.id?.slice(0, 8).toUpperCase()} (${b.car?.make || ""} ${b.car?.model || ""}, ${b.start_date?.slice(0,10)} → ${b.end_date?.slice(0,10)}).`}
                    label="WhatsApp customer"
                    compact
                    testId={`vendor-whatsapp-${b.id}`}
                  />
                </div>
              )}
              {b.pickup_location && (
                <div className="text-xs text-neutral-500 mt-1">
                  Pickup: <span className="text-neutral-700 font-medium">{b.pickup_location}</span> → Drop-off: <span className="text-neutral-700 font-medium">{b.dropoff_location}</span>
                </div>
              )}
              {/* Iter 67bs — attribution chips (creator + last editor + cancel actor). */}
              <AttributionChips entity={b} className="mt-2" />
              {/* Iter 67bs — referral attribution. Tells the host this booking came from a hotel/agency partner. */}
              {b.referrer_name && (
                <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-pink-50 ring-1 ring-pink-200 text-pink-900 text-[10px] font-bold uppercase tracking-wider" data-testid={`booking-referrer-${b.id}`} title={`Referred by ${b.referrer_name} (${b.referrer_code}) — ${b.referral_percent}% commission owed`}>
                  Via {b.referrer_name} · {b.referral_percent}% · <Money amount={b.referral_commission_amount} />
                </div>
              )}
            </div>
            <div className="flex items-start justify-between sm:justify-end gap-2 sm:text-right shrink-0 border-t border-dashed border-border pt-3 sm:border-0 sm:pt-0">
              <div>
                <BookingStatusBadge booking={b} />
                {b.payment_status === "refunded" && (
                  <span className="block mt-1"><BookingStatusBadge status="refunded" /></span>
                )}
                <Money amount={b.amount} className="text-sm font-medium mt-1 block" />
                <Link to={`/agreements/${b.id}`} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-1.5" data-testid={`vendor-booking-agreement-${b.id}`}>
                  <FileText className="h-3 w-3" /> Agreement
                </Link>
                {/* Iter 67eo — open-tracking signal. Green pill when the
                    customer has tapped the WhatsApp PDF share link, so
                    the vendor can stop arguing about whether the
                    agreement landed. Tooltip surfaces the exact UTC
                    timestamp the open was recorded. */}
                {b.agreement_viewed_at && (
                  <span
                    className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 text-[10px] font-semibold"
                    title={`Customer opened the agreement on ${new Date(b.agreement_viewed_at).toLocaleString()}`}
                    data-testid={`agreement-seen-${b.id}`}
                  >
                    ✓ Agreement seen
                  </span>
                )}
                {/* Iter 67ei — vehicle swap trigger. Hidden on terminal-status bookings by the component itself. */}
                <div className="mt-1.5">
                  <VehicleSwapDialog
                    booking={b}
                    onSwapped={() => onChanged?.()}
                    buttonLabel="Swap car"
                    buttonClassName="rounded-full h-7 text-[11px]"
                  />
                </div>
                {/* Iter 67ez — Albania EasyPos fiscalize button. Self-hides for non-Albanian vendors. */}
                <BookingFiscalizeButton booking={b} onChanged={onChanged} />
              </div>
              <BookingActionsMenu booking={b} onChanged={onChanged} />
            </div>
          </div>

          {b.deposit > 0 && b.status === "confirmed" && (
            <div className="mt-4 pt-4 border-t border-border flex items-center justify-between gap-3 flex-wrap" data-testid={`deposit-panel-${b.id}`}>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold flex items-center gap-2">
                  Deposit · <Money amount={b.deposit} />
                  {b.deposit_status === "released" && <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 text-[10px] font-semibold">Released</span>}
                  {b.deposit_status === "withheld" && <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-100 text-[10px] font-semibold">Withheld</span>}
                  {(!b.deposit_status || b.deposit_status === "held") && <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-100 text-[10px] font-semibold">Held</span>}
                </div>
                {b.deposit_status === "withheld" && (
                  <div className="text-xs text-neutral-600 mt-1.5 max-w-md space-y-1">
                    <div>
                      Withheld <Money amount={b.deposit_withheld_amount ?? b.deposit} className="font-semibold text-rose-700" />
                      {Number(b.deposit_refund_amount || 0) > 0 && (
                        <> · refunded <Money amount={b.deposit_refund_amount} className="font-semibold text-emerald-700" /></>
                      )}
                    </div>
                    {b.deposit_decision_reason && (
                      <div className="text-neutral-500">Reason: {b.deposit_decision_reason}</div>
                    )}
                    {Array.isArray(b.deposit_evidence_paths) && b.deposit_evidence_paths.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1.5">
                        {b.deposit_evidence_paths.map((p) => (
                          <a
                            key={p}
                            href={p.startsWith("http") ? p : fileUrl(p)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                            data-testid={`evidence-thumb-${b.id}`}
                          >
                            <img src={p.startsWith("http") ? p : fileUrl(p)} alt="evidence" className="h-12 w-12 rounded-md object-cover border border-border" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {b.deposit_status === "released" && (
                  <div className="text-xs text-neutral-500 mt-1">Refund issued to the customer.</div>
                )}
              </div>
              {(!b.deposit_status || b.deposit_status === "held") && (
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onRelease(b)}
                    className="rounded-full"
                    data-testid={`release-deposit-${b.id}`}
                    disabled={depositLoadingId === b.id}
                  >
                    {depositLoadingId === b.id ? "Refunding…" : "Release"}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onWithhold(b)}
                    className="rounded-full bg-rose-600 hover:bg-rose-700"
                    data-testid={`withhold-deposit-${b.id}`}
                  >
                    Withhold
                  </Button>
                </div>
              )}
            </div>
          )}
          <BookingLogisticsCard booking={b} vendorId={b.vendor_id} onSaved={onChanged} />
          <BookingExtrasPanel booking={b} />
          <BookingMessagesPanel
            bookingId={b.id}
            counterpartyName={b.customer?.name}
            onUnreadChange={onUnreadChange}
          />
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
